import { useEffect, useState } from "react";
import axios from "axios";
import { Authorization } from "../../Context/Authorization";

const PER_VFC_AMOUNT = 350;
const GST             = 0.18;
const TOTAL_PER_VFC   = Math.round(PER_VFC_AMOUNT + PER_VFC_AMOUNT * GST);

/* ── small helpers ── */
const Badge = ({ paid }) => (
  <span style={{
    display: "inline-block", fontSize: 10, fontWeight: 700,
    padding: "2px 9px", borderRadius: 20, letterSpacing: 0.4,
    background: paid ? "#E8F5E9" : "#FFF3CD",
    color: paid ? "#1B5E20" : "#856404",
    border: `1px solid ${paid ? "#C8E6C9" : "#FFE08A"}`,
  }}>
    {paid ? "Paid" : "Pending"}
  </span>
);

const InfoRow = ({ icon, label, value, accent }) => (
  <div style={{
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "10px 0", borderBottom: "1px solid #F0F4F7",
  }}>
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <i className={`mdi mdi-${icon}`} style={{ color: "#718096", fontSize: 15 }} />
      <span style={{ fontSize: 13, color: "#4A5568" }}>{label}</span>
    </div>
    <span style={{ fontSize: 13, fontWeight: 700, color: accent || "#1A202C" }}>{value}</span>
  </div>
);

const isTestMode = process.env.REACT_APP_RAZORPAY_MODE !== "prod";

const ServiceChargePaymentCheck = ({ onComplete }) => {
  const Api = process.env.REACT_APP_API;
  const [loading, setLoading] = useState(false);
  const [data, setData]       = useState([]);

  const TOTAL_AMOUNT = TOTAL_PER_VFC * data.reduce((s, r) => s + (r.unpaidVfcs || 0), 0);

  useEffect(() => { fetchStatus(); }, []);

  const fetchStatus = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${Api}/api/check/service-charge-status`, Authorization());
      const d   = res.data || [];
      setData(d);
      if (!d.some(r => r.unpaidVfcs > 0)) onComplete?.();
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handlePayNow = async () => {
    // Use only UNPAID vfcIds, not all vfcIds
    const unpaidIds = data.filter(r => r.unpaidVfcs > 0).flatMap(r => r.unpaidVfcIds || []);
    if (!unpaidIds.length) { alert("All charges are already paid."); return; }
    const totalAmount = unpaidIds.length * TOTAL_PER_VFC;
    setLoading(true);
    try {
      const res = await axios.post(`${Api}/api/razorpay/create-order`,
        { amount: totalAmount, vfcIds: unpaidIds, totalAmount }, Authorization());
      const { id: order_id, amount, keyId } = res.data;
      new window.Razorpay({
        key: keyId,
        amount, currency: "INR", name: "VFC Annual Fee", order_id,
        theme: { color: "#2E7D32" },
        handler: async (response) => {
          try {
            // Authorization() is required — without it verify-payment returns 401
            await axios.post(`${Api}/api/razorpay/verify-payment`, {
              order_id,
              payment_id: response.razorpay_payment_id,
              signature:  response.razorpay_signature,
            }, Authorization());
            await fetchStatus(); // re-check; if all paid popup will close via onComplete
          } catch (err) {
            console.error("verify-payment failed:", err);
            alert("Payment was received but verification failed. Please contact support.");
          }
        },
        modal: {
          ondismiss: () => {
            axios.post(`${Api}/api/razorpay/payment-failed`,
              { orderId: order_id }, Authorization()).catch(() => {});
          },
        },
      }).open();
    } catch (err) {
      console.error(err);
      alert("Payment failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (!data.some(r => r.unpaidVfcs > 0)) return null;

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 1060,
      background: "rgba(15,20,30,0.65)",
      backdropFilter: "blur(4px)",
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: 16,
    }}>
      <div style={{
        background: "#fff", borderRadius: 18, width: "100%", maxWidth: 720,
        boxShadow: "0 24px 80px rgba(0,0,0,0.3)",
        overflow: "hidden", maxHeight: "92vh", display: "flex", flexDirection: "column",
      }}>

        {/* ── Header ── */}
        <div style={{
          background: "linear-gradient(135deg,#1B5E20,#2E7D32,#388E3C)",
          padding: "20px 24px",
          display: "flex", alignItems: "center", gap: 14, flexShrink: 0,
        }}>
          <div style={{
            width: 48, height: 48, borderRadius: 14,
            background: "rgba(255,255,255,0.15)",
            display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
          }}>
            <i className="mdi mdi-cash-multiple" style={{ color: "#fff", fontSize: 26 }} />
          </div>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{ fontSize: 16, fontWeight: 800, color: "#fff", lineHeight: 1.2 }}>
                Annual Service Charge Due
              </div>
              {isTestMode && (
                <span style={{
                  background: "rgba(255,243,205,0.9)", color: "#856404",
                  fontSize: 10, fontWeight: 700, borderRadius: 20,
                  padding: "2px 8px", letterSpacing: 0.4,
                }}>
                  TEST MODE
                </span>
              )}
            </div>
            <div style={{ fontSize: 12, color: "rgba(255,255,255,0.72)", marginTop: 3 }}>
              Complete payment to continue using the platform
            </div>
          </div>
          <div style={{ marginLeft: "auto" }}>
            <span style={{
              background: "rgba(255,255,255,0.18)", color: "#fff",
              fontSize: 11, fontWeight: 700, borderRadius: 20, padding: "4px 12px",
              border: "1px solid rgba(255,255,255,0.25)",
            }}>
              Action Required
            </span>
          </div>
        </div>

        {/* ── Body ── */}
        <div style={{ overflowY: "auto", padding: "20px 24px", flex: 1 }}>

          {loading ? (
            <div style={{ textAlign: "center", padding: "40px 0", color: "#A0AEC0" }}>
              <i className="mdi mdi-loading mdi-spin" style={{ fontSize: 36, display: "block", marginBottom: 12 }} />
              <div style={{ fontSize: 13 }}>Processing payment…</div>
            </div>
          ) : (
            <>
              {/* Alert banner */}
              <div style={{
                background: "#FFF8E1", border: "1px solid #FFE082",
                borderRadius: 10, padding: "12px 16px", marginBottom: 20,
                display: "flex", gap: 10, alignItems: "flex-start",
              }}>
                <i className="mdi mdi-alert-circle" style={{ color: "#F59E0B", fontSize: 18, flexShrink: 0, marginTop: 1 }} />
                <div style={{ fontSize: 12, color: "#78350F", lineHeight: 1.6 }}>
                  <strong>Annual service charge is mandatory</strong> for all active VFCs. Unpaid VFCs may
                  restrict loan processing, reporting, and approval activities.
                  Payment is required <strong>once per financial year</strong>.
                </div>
              </div>

              {/* Per-range cards */}
              {data.filter(r => r.unpaidVfcs > 0).map((r) => (
                <div key={r.rangeId} style={{
                  border: "1px solid #E2E8F0", borderRadius: 12,
                  overflow: "hidden", marginBottom: 16,
                }}>
                  {/* range header */}
                  <div style={{
                    background: "#F8FAFC", padding: "12px 16px",
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    borderBottom: "1px solid #E2E8F0",
                  }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <i className="mdi mdi-map-marker" style={{ color: "#2E7D32", fontSize: 16 }} />
                      <span style={{ fontSize: 14, fontWeight: 700, color: "#1A202C" }}>{r.rangeName}</span>
                    </div>
                    <Badge paid={r.isFullyPaid} />
                  </div>

                  {/* stat row */}
                  <div style={{
                    display: "grid", gridTemplateColumns: "repeat(4,1fr)",
                    gap: 0, borderBottom: "1px solid #E2E8F0",
                  }}>
                    {[
                      { label: "Total VFCs",   value: r.totalVfcs,  color: "#1A202C" },
                      { label: "Paid",          value: r.paidVfcs,   color: "#2E7D32" },
                      { label: "Pending",       value: r.unpaidVfcs, color: "#D97706" },
                      { label: "Amount Due",    value: `₹ ${(r.unpaidVfcs * TOTAL_PER_VFC).toLocaleString("en-IN")}`, color: "#E53E3E" },
                    ].map((s, i) => (
                      <div key={s.label} style={{
                        padding: "12px 16px", textAlign: "center",
                        borderRight: i < 3 ? "1px solid #E2E8F0" : "none",
                      }}>
                        <div style={{ fontSize: 18, fontWeight: 800, color: s.color }}>{s.value}</div>
                        <div style={{ fontSize: 10, color: "#A0AEC0", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, marginTop: 3 }}>{s.label}</div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              {/* Fee breakdown */}
              <div style={{
                background: "#F8FAFC", border: "1px solid #E2E8F0",
                borderRadius: 12, padding: "16px 20px", marginBottom: 20,
              }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#718096", textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 12 }}>
                  Fee Breakdown
                </div>
                <InfoRow icon="cash"              label="Base Amount per VFC"   value={`₹ ${PER_VFC_AMOUNT}`} />
                <InfoRow icon="percent"           label="GST (18%)"             value={`₹ ${Math.round(PER_VFC_AMOUNT * GST)}`} />
                <InfoRow icon="tag"               label="Total per VFC"         value={`₹ ${TOTAL_PER_VFC}`} accent="#2E7D32" />
                <div style={{ padding: "12px 0 0", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <span style={{ fontSize: 14, fontWeight: 700, color: "#1A202C" }}>Grand Total (All Pending VFCs)</span>
                  <span style={{ fontSize: 20, fontWeight: 800, color: "#1B5E20" }}>
                    ₹ {TOTAL_AMOUNT.toLocaleString("en-IN")}
                  </span>
                </div>
              </div>

              {/* CTA */}
              <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                <button onClick={handlePayNow} style={{
                  flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
                  padding: "14px 24px",
                  background: "linear-gradient(135deg,#1B5E20,#2E7D32)",
                  color: "#fff", border: "none", borderRadius: 10,
                  fontSize: 14, fontWeight: 700, cursor: "pointer",
                  boxShadow: "0 4px 14px rgba(46,125,50,0.4)",
                  transition: "opacity 0.15s",
                }}>
                  <i className="mdi mdi-cash" style={{ fontSize: 20 }} />
                  Pay Now — ₹ {TOTAL_AMOUNT.toLocaleString("en-IN")}
                </button>
                <button onClick={onComplete} style={{
                  padding: "14px 20px",
                  background: "#fff", color: "#718096",
                  border: "1px solid #E2E8F0", borderRadius: 10,
                  fontSize: 13, fontWeight: 600, cursor: "pointer",
                  whiteSpace: "nowrap",
                }}>
                  Pay Later
                </button>
              </div>
              <p style={{ textAlign: "center", fontSize: 11, color: "#A0AEC0", marginTop: 10 }}>
                Choosing "Pay Later" will allow limited access. Some features may be restricted.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ServiceChargePaymentCheck;
